import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, MapPin, CloudSun, Flag } from "lucide-react";
import Markdown from "@/components/Markdown";

export function RaceReportsListPage() {
  const [items, setItems] = useState([]);
  const [splits, setSplits] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [filterSplit, setFilterSplit] = useState("all");
  const [filterSeason, setFilterSeason] = useState("all");

  useEffect(() => {
    api.get("/race-reports?published_only=true").then(x => setItems(x.data || [])).catch(() => {});
    api.get("/splits").then(x => setSplits(x.data || [])).catch(() => {});
    api.get("/seasons").then(x => setSeasons(x.data || [])).catch(() => {});
  }, []);

  const splitColor = (key) => splits.find(s => s.key === key)?.color || "#00E5FF";
  const splitName = (key) => splits.find(s => s.key === key)?.name || key;

  // Saisons mit Berichten — wenn ein Split aktiv ist, nur dessen Saisons
  const seasonOptions = useMemo(() => {
    const relevantItems = filterSplit === "all" ? items : items.filter(r => r.split === filterSplit);
    const ids = new Set(relevantItems.map(r => r.season_id).filter(Boolean));
    return seasons
      .filter(s => ids.has(s.id))
      .filter(s => filterSplit === "all" || (s.split || "champions") === filterSplit)
      .sort((a, b) => (b.year || 0) - (a.year || 0));
  }, [items, seasons, filterSplit]);

  // Wenn die gewählte Saison nicht (mehr) im Filter-Pool ist → reset
  useEffect(() => {
    if (filterSeason !== "all" && !seasonOptions.some(s => s.id === filterSeason)) {
      setFilterSeason("all");
    }
  }, [seasonOptions, filterSeason]);

  const visible = items.filter(r =>
    (filterSplit === "all" || r.split === filterSplit) &&
    (filterSeason === "all" || r.season_id === filterSeason)
  );

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14" data-testid="races-list-page">
      <div className="h-1 w-12 bg-[#00E5FF] mb-4" />
      <h1 className="font-display font-black italic uppercase tracking-normal text-5xl sm:text-7xl leading-none">Rennberichte</h1>
      <p className="text-neutral-400 mt-3 max-w-2xl">Alle Berichte aus der laufenden und vergangenen Saisons der Soest Gaming League.</p>

      {(splits.length > 0 || seasonOptions.length > 0) && (
        <div className="flex flex-wrap items-center gap-4 mt-8" data-testid="races-filter-bar">
          {splits.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilterSplit("all")}
                className="text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors"
                style={filterSplit === "all" ? { borderColor: "#00E5FF", background: "rgba(0,229,255,0.1)", color: "#00E5FF" } : { borderColor: "rgba(255,255,255,0.15)", color: "#a3a3a3" }}
                data-testid="races-filter-split-all"
              >Alle ({items.length})</button>
              {splits.map(s => {
                const count = items.filter(r => r.split === s.key).length;
                const active = filterSplit === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setFilterSplit(s.key)}
                    className="text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors"
                    style={active ? { borderColor: s.color, background: `${s.color}1A`, color: s.color } : { borderColor: "rgba(255,255,255,0.15)", color: "#a3a3a3" }}
                    data-testid={`races-filter-split-${s.key}`}
                  >{s.name} ({count})</button>
                );
              })}
            </div>
          )}
          {seasonOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Saison</span>
              <select
                value={filterSeason}
                onChange={e => setFilterSeason(e.target.value)}
                className="bg-[#0B0F19] border border-white/10 px-3 py-1.5 text-xs text-neutral-200"
                data-testid="races-filter-season"
              >
                <option value="all">Alle</option>
                {seasonOptions.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.year ? ` · ${s.year}` : ""}{s.game ? ` · ${s.game}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        {visible.map((r) => {
          const sc = splitColor(r.split);
          return (
            <Link key={r.id} to={`/rennberichte/${r.slug}`} className="card-surface block group overflow-hidden" data-testid={`race-card-${r.id}`}>
              <div className="relative h-52 overflow-hidden">
                {r.hero_image ? <img src={r.hero_image} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" /> :
                  <div className="w-full h-full bg-gradient-to-br from-[#1F1F1F] to-[#0B0F19]" />}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-transparent to-transparent" />
                {r.split && (
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2 py-1 backdrop-blur-sm" style={{ background: `${sc}22`, color: sc, border: `1px solid ${sc}66` }}>
                    {splitName(r.split)}
                  </span>
                )}
              </div>
              <div className="p-5">
                <div className="eyebrow">{r.race_name}</div>
                <div className="font-display font-black italic uppercase text-xl mt-2 leading-tight group-hover:text-[#00E5FF]">{r.title}</div>
                <p className="text-sm text-neutral-400 mt-2 line-clamp-3">{r.summary}</p>
                <div className="flex items-center gap-4 mt-4 text-xs text-neutral-500">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{r.date}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{r.track}</span>
                </div>
              </div>
            </Link>
          );
        })}
        {visible.length === 0 && <div className="text-neutral-500">Keine Rennberichte in dieser Auswahl.</div>}
      </div>
    </div>
  );
}

export function RaceReportDetailPage() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/race-reports/${slug}`).then(x => setR(x.data)).catch(e => setErr(e?.response?.data?.detail || "Nicht gefunden")); }, [slug]);
  if (err) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-400" data-testid="race-not-found">{String(err)}</div>;
  if (!r) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-400">Lade...</div>;
  return (
    <article className="pb-20" data-testid="race-detail-page">
      <div className="relative h-[55vh] min-h-[400px] overflow-hidden">
        {r.hero_image && <img src={r.hero_image} alt={r.title} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 max-w-5xl mx-auto px-6 md:px-10 pb-10">
          <div className="h-1 w-16 bg-[#00E5FF] mb-4" />
          <div className="eyebrow text-[#00E5FF]">{r.race_name}</div>
          <h1 className="font-display font-black italic uppercase tracking-normal text-4xl sm:text-6xl leading-none mt-2"
            style={{ textShadow: "0 4px 24px rgba(0,0,0,0.8)" }}>{r.title}</h1>
          <div className="flex flex-wrap gap-6 mt-5 text-sm text-neutral-200">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#00E5FF]" />{r.date}</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#00E5FF]" />{r.track}</span>
            <span className="flex items-center gap-2"><CloudSun className="w-4 h-4 text-[#00E5FF]" />{r.weather}</span>
            <span className="flex items-center gap-2"><Flag className="w-4 h-4 text-[#00E5FF]" />{r.author}</span>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 md:px-10 mt-10">
        <p className="text-2xl text-white font-medium leading-snug mb-8">{r.summary}</p>
        <Markdown>{r.body}</Markdown>
      </div>
    </article>
  );
}
